import React, { useState } from "react";
import { Tabs, Tab, Button, Box } from "@mui/material";
import { showSuccessToast } from "../../../constants/ToastService";
import "../../SharedTable.css";

const listOrEmpty = (v) => (Array.isArray(v) ? v : v ? [v] : []);

const safeStr = (x) => (x != null ? String(x) : "—");
const safeList = (v) => listOrEmpty(v).filter((x) => x != null);

const NormalizedPanel = ({ normalized }) => {
  if (!normalized || typeof normalized !== "object") return <p style={{ color: "#888" }}>No data.</p>;
  const fields = safeList(normalized.fields).filter((f) => f && typeof f === "object");
  return (
    <div className="req-artifact-panel">
      <div className="req-artifact-section">
        <h4>Summary</h4>
        <p style={{ margin: 0 }}>{normalized.summary || "—"}</p>
      </div>
      <div className="req-artifact-section">
        <h4>Goal</h4>
        <p style={{ margin: 0 }}>{normalized.goal || "—"}</p>
      </div>
      {listOrEmpty(normalized.actors).length > 0 && (
        <div className="req-artifact-section">
          <h4>Actors</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(normalized.actors).map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(normalized.in_scope).length > 0 && (
        <div className="req-artifact-section">
          <h4>In scope</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(normalized.in_scope).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(normalized.out_of_scope).length > 0 && (
        <div className="req-artifact-section">
          <h4>Out of scope</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(normalized.out_of_scope).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(normalized.user_flows).length > 0 && (
        <div className="req-artifact-section">
          <h4>User flows</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(normalized.user_flows).map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(normalized.fields).length > 0 && fields.length > 0 && (
        <div className="req-artifact-section">
          <h4>Fields</h4>
          <ul className="req-artifact-list">
            {fields.map((f, i) => (
              <li key={i}>
                <strong>{safeStr(f.name)}</strong> ({safeStr(f.type)}) {f.required ? "required" : ""}
                {Array.isArray(f.validations) && f.validations.length ? ` — ${f.validations.join("; ")}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(normalized.business_rules).length > 0 && (
        <div className="req-artifact-section">
          <h4>Business rules</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(normalized.business_rules).map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(normalized.assumptions).length > 0 && (
        <div className="req-artifact-section">
          <h4>Assumptions</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(normalized.assumptions).map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const DevSpecPanel = ({ devSpec, unitTests }) => {
  if (!devSpec || typeof devSpec !== "object") return <p style={{ color: "#888" }}>No data.</p>;
  const handleCopy = () => {
    try {
      const payload = unitTests && typeof unitTests === "object" ? { ...devSpec, unit_tests: unitTests } : devSpec;
      navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
      showSuccessToast("Dev Spec copied to clipboard");
    } catch {
      showSuccessToast("Copy failed");
    }
  };
  const unitTestCases = unitTests && typeof unitTests === "object" ? safeList(unitTests.unit_test_cases).filter((ut) => ut && typeof ut === "object") : [];
  const testFrameworkSuggestion = unitTests && typeof unitTests === "object" ? unitTests.test_framework_suggestion : null;
  return (
    <div className="req-artifact-panel">
      <Button size="small" variant="outlined" onClick={handleCopy} className="req-copy-btn" sx={{ textTransform: "none", marginBottom: 1 }}>
        Copy JSON
      </Button>
      <div className="req-artifact-section">
        <h4>Summary</h4>
        <p style={{ margin: 0 }}>{devSpec.summary || "—"}</p>
      </div>
      {listOrEmpty(devSpec.functional_requirements).length > 0 && (
        <div className="req-artifact-section">
          <h4>Functional requirements</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(devSpec.functional_requirements).map((r, i) => (
              <li key={i}>{r}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(devSpec.ui_notes).length > 0 && (
        <div className="req-artifact-section">
          <h4>UI notes</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(devSpec.ui_notes).map((n, i) => (
              <li key={i}>{n}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(devSpec.impact_analysis).length > 0 && (
        <div className="req-artifact-section">
          <h4>Impact analysis</h4>
          {listOrEmpty(devSpec.impact_analysis)
            .filter((item) => item && typeof item === "object")
            .map((item, i) => (
              <div key={item.area != null ? item.area : i} style={{ marginBottom: 8 }}>
                <strong>{safeStr(item.area)}</strong>
                <ul className="req-artifact-list">
                  {listOrEmpty(item.changes).map((c, j) => (
                    <li key={j}>{c != null ? String(c) : ""}</li>
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}
      {listOrEmpty(devSpec.error_messages).length > 0 && (
        <div className="req-artifact-section">
          <h4>Error messages</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(devSpec.error_messages).map((e, i) => (
              <li key={i}>
                [{e && typeof e === "object" ? e.code : ""}] {e && typeof e === "object" ? e.message : ""}
                {e && typeof e === "object" && e.when ? ` — when: ${e.when}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(devSpec.acceptance_criteria).length > 0 && (
        <div className="req-artifact-section">
          <h4>Acceptance criteria</h4>
          <table className="fd-table fd-table--nested" style={{ marginTop: 8 }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Given</th>
                <th>When</th>
                <th>Then</th>
              </tr>
            </thead>
            <tbody>
              {listOrEmpty(devSpec.acceptance_criteria)
                .filter((ac) => ac && typeof ac === "object")
                .map((ac, i) => (
                  <tr key={ac.id != null ? ac.id : i}>
                    <td>{safeStr(ac.id)}</td>
                    <td>{safeStr(ac.given)}</td>
                    <td>{safeStr(ac.when)}</td>
                    <td>{safeStr(ac.then)}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {listOrEmpty(devSpec.edge_cases).length > 0 && (
        <div className="req-artifact-section">
          <h4>Edge cases</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(devSpec.edge_cases).map((e, i) => (
              <li key={i}>{e}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(devSpec.definition_of_done).length > 0 && (
        <div className="req-artifact-section">
          <h4>Definition of done</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(devSpec.definition_of_done).map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        </div>
      )}
      {unitTestCases.length > 0 && (
        <div className="req-artifact-section">
          <h4>Unit test cases</h4>
          {testFrameworkSuggestion && (
            <p style={{ margin: "0 0 12px 0", color: "#888", fontSize: "0.875rem" }}>
              Suggested framework: <strong>{safeStr(testFrameworkSuggestion)}</strong>
            </p>
          )}
          <table className="fd-table fd-table--nested">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Priority</th>
                <th>Target</th>
                <th>Description</th>
                <th>Expected behavior</th>
                <th>Assertions</th>
                <th>Edge cases</th>
                <th>Maps to AC</th>
              </tr>
            </thead>
            <tbody>
              {unitTestCases.map((ut, i) => (
                <tr key={ut.id != null ? ut.id : i}>
                  <td>{safeStr(ut.id)}</td>
                  <td>{safeStr(ut.title)}</td>
                  <td>{safeStr(ut.priority)}</td>
                  <td>{safeStr(ut.target)}</td>
                  <td>{safeStr(ut.description)}</td>
                  <td>{safeStr(ut.expected_behavior)}</td>
                  <td>
                    <ul className="results-list">
                      {listOrEmpty(ut.assertions).map((a, j) => (
                        <li key={j}>{a != null ? String(a) : ""}</li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <ul className="results-list">
                      {listOrEmpty(ut.edge_cases_covered).map((e, j) => (
                        <li key={j}>{e != null ? String(e) : ""}</li>
                      ))}
                    </ul>
                  </td>
                  <td>{listOrEmpty(ut.maps_to_acceptance_criteria).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const QAPlanPanel = ({ qaPlan }) => {
  if (!qaPlan || typeof qaPlan !== "object") return <p style={{ color: "#888" }}>No data.</p>;
  const cases = safeList(qaPlan.test_cases).filter((tc) => tc && typeof tc === "object");
  const handleCopy = () => {
    try {
      navigator.clipboard.writeText(JSON.stringify(qaPlan, null, 2));
      showSuccessToast("QA Plan copied to clipboard");
    } catch {
      showSuccessToast("Copy failed");
    }
  };
  return (
    <div className="req-artifact-panel">
      <Button size="small" variant="outlined" onClick={handleCopy} className="req-copy-btn" sx={{ textTransform: "none", marginBottom: 1 }}>
        Copy JSON
      </Button>
      {listOrEmpty(qaPlan.test_strategy).length > 0 && (
        <div className="req-artifact-section">
          <h4>Test strategy</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(qaPlan.test_strategy).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(qaPlan.test_scenarios).length > 0 && (
        <div className="req-artifact-section">
          <h4>Test scenarios</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(qaPlan.test_scenarios).map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}
      {listOrEmpty(qaPlan.coverage_map).length > 0 && (
        <div className="req-artifact-section">
          <h4>Coverage map</h4>
          <ul className="req-artifact-list">
            {listOrEmpty(qaPlan.coverage_map)
              .filter((c) => c && typeof c === "object")
              .map((c, i) => (
                <li key={c.area != null ? c.area : i}>
                  <strong>{safeStr(c.area)}</strong>: {listOrEmpty(c.test_cases).join(", ")}
                </li>
              ))}
          </ul>
        </div>
      )}
      {cases.length > 0 && (
        <div className="req-artifact-section">
          <h4>Test cases</h4>
          <table className="fd-table fd-table--nested">
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Steps</th>
                <th>Expected results</th>
                <th>Maps to AC</th>
              </tr>
            </thead>
            <tbody>
              {cases.map((tc, i) => (
                <tr key={tc.id != null ? tc.id : i}>
                  <td>{safeStr(tc.id)}</td>
                  <td>{safeStr(tc.title)}</td>
                  <td>{safeStr(tc.type)}</td>
                  <td>{safeStr(tc.priority)}</td>
                  <td>
                    <ol className="steps-list">
                      {listOrEmpty(tc.steps).map((s, j) => (
                        <li key={j}>{s != null ? String(s) : ""}</li>
                      ))}
                    </ol>
                  </td>
                  <td>
                    <ul className="results-list">
                      {listOrEmpty(tc.expected_results).map((r, j) => (
                        <li key={j}>{r != null ? String(r) : ""}</li>
                      ))}
                    </ul>
                  </td>
                  <td>{listOrEmpty(tc.maps_to_acceptance_criteria).join(", ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ArtifactsView = ({ artifact }) => {
  const [tab, setTab] = useState(0);
  if (!artifact || typeof artifact !== "object") return null;
  const tabIndex = Number(tab) || 0;
  return (
    <div className="req-detail-section">
      <h3 className="req-detail-section-title">Artifacts</h3>
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTab(typeof v === "number" ? v : 0)}
        className="req-artifacts-tabs"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Normalized" />
        <Tab label="Dev Spec" />
        <Tab label="QA Plan" />
      </Tabs>
      <Box sx={{ pt: 1 }}>
        {tabIndex === 0 && <NormalizedPanel normalized={artifact.normalized} />}
        {tabIndex === 1 && <DevSpecPanel devSpec={artifact.dev_spec} unitTests={artifact.unit_tests} />}
        {tabIndex === 2 && <QAPlanPanel qaPlan={artifact.qa_plan} />}
      </Box>
    </div>
  );
};

export default ArtifactsView;
